import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone } from 'lucide-react';

interface PersistentCallBarProps {
  taskId: string;
  company: string;
  status: 'calling' | 'in_progress' | 'on_hold';
  duration: number; // seconds elapsed
  onNavigateToCall: () => void;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function PersistentCallBar({
  company,
  status,
  duration,
  onNavigateToCall,
}: PersistentCallBarProps) {
  const [elapsed, setElapsed] = useState(duration);

  useEffect(() => {
    setElapsed(duration);
    const interval = setInterval(() => {
      setElapsed((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [duration]);

  const statusLabel =
    status === 'calling'
      ? 'Calling'
      : status === 'on_hold'
        ? 'On hold with'
        : 'On call with';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -40, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: '40px',
          background: 'linear-gradient(90deg, rgba(88, 28, 135, 0.95) 0%, rgba(30, 27, 75, 0.95) 100%)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 1rem',
          zIndex: 10000,
          borderBottom: '1px solid rgba(var(--primary-rgb), 0.3)',
          cursor: 'pointer',
        }}
        onClick={onNavigateToCall}
        role="button"
        tabIndex={0}
        aria-label={`${statusLabel} ${company}. Click to view call.`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onNavigateToCall();
          }
        }}
      >
        {/* Left: pulsing dot + status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
          <span
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: '#22c55e',
              flexShrink: 0,
              animation: 'persistent-call-pulse 2s ease-in-out infinite',
            }}
          />
          <Phone size={14} style={{ color: '#c4b5fd', flexShrink: 0 }} />
          <span
            style={{
              color: '#e2e8f0',
              fontSize: '0.8rem',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {statusLabel} {company}
          </span>
        </div>

        {/* Center: timer */}
        <span
          style={{
            color: '#c4b5fd',
            fontSize: '0.8rem',
            fontFamily: 'monospace',
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {formatDuration(elapsed)}
        </span>

        {/* Right: nav button */}
        <span
          style={{
            color: 'var(--primary)',
            fontSize: '0.75rem',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          View Call &rarr;
        </span>

        <style>{`
          @keyframes persistent-call-pulse {
            0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(34,197,94,0.4); }
            50% { opacity: 0.7; box-shadow: 0 0 0 4px rgba(34,197,94,0); }
          }
        `}</style>
      </motion.div>
    </AnimatePresence>
  );
}
